import { currency, fullName } from "../../../../services/utilities";

export const getCoverage = (settlement = {}) =>
  (Array.isArray(settlement?.sponsorship) ? settlement.sponsorship : []).reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0,
  );

export const getGross = (settlement = {}) => {
  const cart = Array.isArray(settlement?.cart) ? settlement.cart : [];
  if (cart.length) {
    return cart.reduce(
      (sum, item) =>
        sum + Number(item?.qty || 0) * Number(item?.srp || item?.amount || 0),
      0,
    );
  }

  return Number(settlement?.amount || 0) + Number(settlement?.discount || 0);
};

export const getDiscount = (settlement = {}) =>
  Number(settlement?.discount || 0);

export const getNet = (settlement = {}) =>
  Math.max(getGross(settlement) - getDiscount(settlement), 0);

export const getShare = (settlement = {}) =>
  Math.max(getNet(settlement) - getCoverage(settlement), 0);

export const getClinicName = (settlement = {}) =>
  settlement?.appointment?.clinic?.title ||
  settlement?.appointment?.clinic?.code ||
  "Clinic";

export const getPhysicianName = (settlement = {}) =>
  fullName(
    settlement?.appointment?.clinic?.physician?.fullName ||
      settlement?.appointment?.clinic?.physician?.user?.fullName,
  ) || "Dr. -";

export const getPatientName = (settlement = {}) =>
  fullName(settlement?.patient?.fullName) || "Unnamed patient";

export const getMenuDosage = (menu = {}) => {
  if (menu?.dosage) return menu.dosage;
  return "";
};

export const getCartLabel = (item = {}) => {
  const menu = item?.menu || {};
  const base =
    menu?.abbreviation || menu?.description || menu?.name || "Unnamed menu";
  const dosage = getMenuDosage(menu);

  return dosage ? `${base} (${dosage})` : base;
};

export const getCollectionRows = (collections = [], searchKey = "", clinicFilter = "all") => {
  const keyword = String(searchKey || "").trim().toLowerCase();

  return (Array.isArray(collections) ? collections : [])
    .filter((item) =>
      clinicFilter === "all"
        ? true
        : String(getClinicName(item)) === String(clinicFilter),
    )
    .map((item) => {
      const gross = getGross(item);
      const discount = getDiscount(item);
      const coverage = getCoverage(item);
      const share = getShare(item);
      const createdAt = item?.createdAt ? new Date(item.createdAt) : null;

      return {
        ...item,
        gross,
        discount,
        coverage,
        share,
        clinicName: getClinicName(item),
        physicianName: getPhysicianName(item),
        patientName: getPatientName(item),
        itemLabels: (Array.isArray(item?.cart) ? item.cart : []).map(getCartLabel),
        timeLabel:
          createdAt && !Number.isNaN(createdAt.getTime())
            ? createdAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "--:--",
      };
    })
    .filter((item) => {
      if (!keyword) return true;

      return [
        item.patientName,
        item.clinicName,
        item.physicianName,
        item.payment,
        item?.appointment?.qn,
        ...item.itemLabels,
      ]
        .join(" ")
        .toLowerCase()
        .includes(keyword);
    });
};

export const getClinicOptions = (collections = []) =>
  Array.from(new Set((Array.isArray(collections) ? collections : []).map(getClinicName)))
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));

export const getAnalytics = (collections = []) => {
  const rows = getCollectionRows(collections);
  const paymentMap = {};
  const clinicMap = {};
  const menuMap = {};

  const summary = rows.reduce(
    (acc, item) => {
      acc.transactions += 1;
      acc.gross += item.gross;
      acc.discount += item.discount;
      acc.coverage += item.coverage;
      acc.share += item.share;

      const paymentKey = String(item?.payment || "cash").toUpperCase();
      paymentMap[paymentKey] = (paymentMap[paymentKey] || 0) + item.share;

      clinicMap[item.clinicName] = (clinicMap[item.clinicName] || 0) + item.share;

      item.itemLabels.forEach((label) => {
        menuMap[label] = (menuMap[label] || 0) + 1;
      });

      return acc;
    },
    {
      transactions: 0,
      gross: 0,
      discount: 0,
      coverage: 0,
      share: 0,
    },
  );

  return {
    ...summary,
    paymentBreakdown: Object.entries(paymentMap).sort((left, right) => right[1] - left[1]),
    clinicBreakdown: Object.entries(clinicMap).sort((left, right) => right[1] - left[1]),
    topMenus: Object.entries(menuMap).sort((left, right) => right[1] - left[1]).slice(0, 10),
  };
};

export const money = (value = 0) => currency.format(Number(value || 0));
